// react-native-mmkv is a JSI-native key/value store. partner-db's @lib/storage
// does `new MMKV()` at module load and uses it as the redux-persist storage
// engine (persistStorage = {getItem,setItem,removeItem}) AND for sync
// get/set/delete/contains. On web the native binding is absent → `new MMKV()`
// throws and the whole app fails to boot. Back it with localStorage so:
//   - redux-persist rehydrates the same `persist:root` key it writes, and
//   - the harness/probe can inject an auth session by writing localStorage.
// Same string contract as real MMKV (getString → string|undefined).
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { checkWebBuild, clearWebBuild } = require("./web-build");
const mem = {};
const backing = (() => {
  try {
    window.localStorage.setItem("__mmkv_t", "1");
    window.localStorage.removeItem("__mmkv_t");
    return window.localStorage;
  } catch (_) {
    return {
      getItem: (k) => (k in mem ? mem[k] : null),
      setItem: (k, v) => {
        mem[k] = String(v);
      },
      removeItem: (k) => {
        delete mem[k];
      },
      clear: () => {
        Object.keys(mem).forEach((k) => delete mem[k]);
      },
    };
  }
})();

class MMKV {
  constructor(config) {
    // Honour an optional instance id so distinct MMKV() instances don't collide.
    this._id = config && config.id ? `mmkv.${config.id}.` : "mmkv.default.";
  }
  _k(key) {
    return this._id + key;
  }
  getString(key) {
    const v = backing.getItem(this._k(key));
    return v == null ? undefined : v;
  }
  getNumber(key) {
    const v = this.getString(key);
    return v == null ? undefined : Number(v);
  }
  getBoolean(key) {
    const v = this.getString(key);
    return v == null ? undefined : v === "true";
  }
  set(key, value) {
    backing.setItem(this._k(key), String(value));
    // D+ per-CP web build routing: a fresh login token → ask which build to run.
    if (key === "user_token") {
      if (value) checkWebBuild(String(value));
      else clearWebBuild();
    }
  }
  delete(key) {
    backing.removeItem(this._k(key));
    if (key === "user_token") clearWebBuild(); // logout → drop the build cookies
  }
  contains(key) {
    return backing.getItem(this._k(key)) != null;
  }
  getAllKeys() {
    const out = [];
    try {
      for (let i = 0; i < backing.length; i++) {
        const full = backing.key(i);
        if (full && full.indexOf(this._id) === 0)
          out.push(full.slice(this._id.length));
      }
    } catch (_) {}
    return out;
  }
  clearAll() {
    this.getAllKeys().forEach((k) => this.delete(k));
  }
  addOnValueChangedListener() {
    return { remove() {} };
  }
  recrypt() {}
}

// NOTE on persist key: @lib/storage prefixes nothing of its own, so redux-persist
// writes under `mmkv.default.persist:root`. The probe injects auth at that exact
// localStorage key. If a future @lib/storage change passes an `id`, update both.
module.exports = {
  __esModule: true,
  MMKV,
  useMMKVString: () => [undefined, () => {}],
  useMMKVNumber: () => [undefined, () => {}],
  useMMKVBoolean: () => [undefined, () => {}],
};
